import React, { useCallback, useMemo, useState } from 'react'
import { Stack } from 'react-bootstrap'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import type { BuildProductResult } from '@ztechapps/common.contract'
import { BuildProductStatusEnum } from '@ztechapps/common.contract'
import { CreateProductProposalCard, type ProductDraft, type ProductProposal } from '../CreateProductProposalCard'
import { useCreateProductCommit } from '../hooks/useCreateProductCommit'

export type CreateProductProposalGroupProps = {
  /** Proposals decoded from the MCP resource(s). */
  proposals: ProductProposal[]
  /**
   * Override the default commit path. The default uses
   * `useCreateProductCommit` which talks to the shared
   * `buildProductFromDraft` engine. PO-side surfaces inject a custom
   * handler so the resolved `_id` can be threaded back into the PO
   * draft items before the PO mutation fires.
   */
  onCommit?: (proposal: ProductProposal, patch: Partial<ProductDraft>) => Promise<BuildProductResult>
  /** Hide the group's "Create all" header — used when the group is nested. */
  hideHeader?: boolean
  /** Callback invoked every time a card transitions to a terminal state. */
  onCardSettled?: (params: {
    proposal: ProductProposal
    patch: Partial<ProductDraft>
    result?: BuildProductResult
  }) => void
}

type CardState = {
  patch: Partial<ProductDraft>
  busy: boolean
  result?: 'created' | 'failed'
  error?: string
  createdProduct?: { _id: string; code: string }
}

const initialState = (): CardState => ({ patch: {}, busy: false })

/**
 * Batch container for one or more `ProductProposal` cards. Renders a
 * "Create all" header that iterates the proposals sequentially so per-card
 * errors surface individually (parallel commits would mask them). Cards
 * with `proposal.duplicate` are skipped — they're "open existing"
 * affordances and there's nothing to commit.
 *
 * Each card owns its own patch / busy / result state in the group's
 * local `states` map; the group is the single source of truth so
 * "Create all" can drive every card without prop-drilling through
 * the consuming chat surface.
 */
export const CreateProductProposalGroup = ({
  proposals,
  onCommit,
  hideHeader = false,
  onCardSettled,
}: CreateProductProposalGroupProps) => {
  const { commit: defaultCommit } = useCreateProductCommit()
  const commit = onCommit ?? defaultCommit

  const [states, setStates] = useState<Record<string, CardState>>(() => initialStates(proposals))
  const [batchRunning, setBatchRunning] = useState(false)

  const updateState = useCallback((key: string, updater: (prev: CardState) => CardState) => {
    setStates((prev) => ({ ...prev, [key]: updater(prev[key] ?? initialState()) }))
  }, [])

  const handleEdit = useCallback(
    (key: string, patch: Partial<ProductDraft>) => {
      updateState(key, (prev) => ({ ...prev, patch: { ...prev.patch, ...patch } }))
    },
    [updateState]
  )

  const commitOne = useCallback(
    async (key: string, proposal: ProductProposal): Promise<BuildProductResult | undefined> => {
      const current = states[key] ?? initialState()
      updateState(key, (prev) => ({ ...prev, busy: true, error: undefined, result: undefined }))
      try {
        const result = await commit(proposal, current.patch)
        if (result.status === BuildProductStatusEnum.Created) {
          updateState(key, (prev) => ({
            ...prev,
            busy: false,
            result: 'created',
            createdProduct: { _id: result._id, code: result.code },
          }))
        } else {
          updateState(key, (prev) => ({
            ...prev,
            busy: false,
            result: 'failed',
            error: result.error ?? 'Failed to create product.',
          }))
        }
        onCardSettled?.({ proposal, patch: current.patch, result })
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        updateState(key, (prev) => ({ ...prev, busy: false, result: 'failed', error: message }))
        onCardSettled?.({ proposal, patch: current.patch })
        return undefined
      }
    },
    [commit, onCardSettled, states, updateState]
  )

  const handleCreateAll = useCallback(async () => {
    if (batchRunning) return
    setBatchRunning(true)
    try {
      for (let i = 0; i < proposals.length; i += 1) {
        const proposal = proposals[i]
        const key = proposalKey(proposal, i)
        if (proposal.duplicate) continue
        const state = states[key]
        if (state?.result === 'created') continue
        await commitOne(key, proposal)
      }
    } finally {
      setBatchRunning(false)
    }
  }, [batchRunning, commitOne, proposals, states])

  const headerLabel = useMemo(() => {
    const count = proposals.length
    return count === 1 ? '1 proposed product' : `${count} proposed products`
  }, [proposals.length])

  const pendingCount = useMemo(
    () =>
      proposals.filter((p, i) => {
        if (p.duplicate) return false
        return states[proposalKey(p, i)]?.result !== 'created'
      }).length,
    [proposals, states]
  )

  return (
    <Stack gap={2}>
      <ShowIf condition={!hideHeader}>
        <HorizontalStack gap={2} className="justify-content-between align-items-center">
          <span className="fw-semibold text-body">{headerLabel}</span>
          <BusyButton
            variant="primary"
            bsSize="sm"
            text="Create all"
            isBusy={batchRunning}
            busyText="Creating…"
            disabled={pendingCount === 0}
            onClick={handleCreateAll}
          />
        </HorizontalStack>
      </ShowIf>

      {proposals.map((proposal, i) => {
        const key = proposalKey(proposal, i)
        const state = states[key] ?? initialState()
        return (
          <CreateProductProposalCard
            key={key}
            proposal={proposal}
            busy={state.busy}
            result={state.result}
            error={state.error}
            createdProduct={state.createdProduct}
            onEdit={(patch) => handleEdit(key, patch)}
            onCreate={async () => {
              await commitOne(key, proposal)
            }}
          />
        )
      })}
    </Stack>
  )
}

const proposalKey = (proposal: ProductProposal, index: number): string =>
  proposal.ref ?? proposal.draft.code ?? `proposal-${index}`

const initialStates = (proposals: ProductProposal[]): Record<string, CardState> => {
  const next: Record<string, CardState> = {}
  proposals.forEach((p, i) => {
    next[proposalKey(p, i)] = initialState()
  })
  return next
}
